import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

// Session 4 — Admin management surface for Island Essentials.
// Island selector tabs at top; 5 management sub-cards for pharmacy /
// urgent care / gas / beach / cruise; each sub-card owns its own state
// and Save button. Sponsors are managed in the existing Ad Slots tab
// (spec — "These appear in the existing Ad Slots section").

const ADMIN_ISLANDS = [
  { slug: "aruba",     name: "Aruba",       flag: "🇦🇼" },
  { slug: "curacao",   name: "Curaçao",     flag: "🇨🇼" },
  { slug: "bonaire",   name: "Bonaire",     flag: "🇧🇶" },
  { slug: "stmaarten", name: "St. Maarten", flag: "🇸🇽" },
];

const Field = ({ label, value, onChange, type = "text", testid, placeholder }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1 block">
      {label}
    </span>
    <input
      type={type}
      value={value || ""}
      placeholder={placeholder || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm"
      data-testid={testid}
    />
  </label>
);

const Toggle = ({ label, value, onChange, testid }) => (
  <label className="flex items-center gap-2 text-xs text-[#1A1A1A]">
    <input
      type="checkbox"
      checked={!!value}
      onChange={(e) => onChange(e.target.checked)}
      data-testid={testid}
    />
    {label}
  </label>
);

const SectionCard = ({ title, subtitle, children, testid }) => (
  <div className="card-soft p-5" data-testid={testid}>
    <div className="mb-3">
      <h3 className="text-[13px] uppercase tracking-wider text-[#0AADA0]">{title}</h3>
      {subtitle && <p className="text-[11px] text-[#6B7280] mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);

export const EssentialsTab = () => {
  const [island, setIsland] = useState("aruba");
  return (
    <div className="space-y-6" data-testid="essentials-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-serif-display text-2xl text-[#1A1A1A]">Island Essentials</h2>
        <p className="text-[11px] text-[#6B7280]">
          Sponsors for each utility are managed in the <b>Ad Slots</b> tab.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="essentials-admin-island-tabs">
        {ADMIN_ISLANDS.map((i) => {
          const active = island === i.slug;
          return (
            <button
              key={i.slug}
              onClick={() => setIsland(i.slug)}
              className={`text-[12px] px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                active
                  ? "bg-[#0AADA0] text-white"
                  : "bg-[#F5F5F5] text-[#1A1A1A] hover:bg-[#EEEEEE]"
              }`}
              data-testid={`essentials-admin-tab-${i.slug}`}
            >
              <span>{i.flag}</span>
              <span>{i.name}</span>
            </button>
          );
        })}
      </div>

      <PharmacyRotationAdmin key={`phr-${island}`} island={island} />
      <PharmacyDirectoryAdmin />
      <PharmacyAdmin  key={`ph-${island}`}  island={island} />
      <BeachAdmin      key={`b-${island}`}   island={island} />
      <CurrencyAdmin   key={`cur-${island}`} island={island} />
      <CruiseAdmin     key={`cr-${island}`}  island={island} />
    </div>
  );
};

// -------------- Pharmacy Directory (2026-08-05) --------------
// The IVA scraper writes name-only rows; this directory is joined
// against those names to supply phone + address. Rows are seeded once
// at boot from boticadiservicio.com's pharmaciesList — admins use this
// form to correct wrong entries (e.g. a pharmacy moved) or add ones
// the alliance directory doesn't include yet. Island-agnostic (the
// directory serves all Aruba scrapers).

const PharmacyDirectoryAdmin = () => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // canonical_key being edited
  const [form, setForm] = useState({ name: "", phone: "", address: "", district: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/pharmacy-directory");
      setRows(r.data.rows || []);
    } catch (_) {
      /* silent */
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (row) => {
    setEditing(row.canonical_key);
    setForm({
      name: row.name || "",
      phone: row.phone || "",
      address: row.address || "",
      district: row.district || "",
    });
  };
  const startAdd = () => {
    setEditing("__new__");
    setForm({ name: "", phone: "", address: "", district: "" });
  };
  const cancel = () => {
    setEditing(null);
    setForm({ name: "", phone: "", address: "", district: "" });
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/pharmacy-directory", form);
      toast.success("Directory entry saved");
      setEditing(null);
      setForm({ name: "", phone: "", address: "", district: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}" from directory?`)) return;
    try {
      await api.delete("/admin/pharmacy-directory", { params: { name: row.name } });
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <SectionCard
      title="💊 Pharmacy Directory · Contact info for IVA calendar"
      subtitle="Names come from iva.aw; phone + address come from here. Seeded from Botica di Servicio at boot; admin edits win. Fix any entry that goes stale or wrong."
      testid="essentials-admin-pharmacy-directory"
    >
      {editing ? (
        <div className="p-3 rounded-lg border border-[#0AADA0] bg-[#F0FDFA] mb-4">
          <div className="text-[11px] uppercase tracking-wider text-[#0AADA0] font-semibold mb-2">
            {editing === "__new__" ? "Add new entry" : "Editing entry"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
            <Field label="Pharmacy name *" value={form.name}
                   onChange={(v) => setForm({ ...form, name: v })}
                   testid="phdir-name" />
            <Field label="Phone" value={form.phone}
                   onChange={(v) => setForm({ ...form, phone: v })}
                   testid="phdir-phone"
                   placeholder="+297 582 1253" />
            <Field label="Address" value={form.address}
                   onChange={(v) => setForm({ ...form, address: v })}
                   testid="phdir-address" />
            <Field label="District" value={form.district}
                   onChange={(v) => setForm({ ...form, district: v })}
                   testid="phdir-district"
                   placeholder="Oranjestad / San Nicolas / Noord" />
          </div>
          <div className="flex gap-2">
            <button
              onClick={save} disabled={saving}
              className="px-4 py-2 rounded-lg bg-[#0AADA0] text-white text-sm font-medium disabled:opacity-60"
              data-testid="phdir-save"
            >
              {saving ? "Saving..." : "Save entry"}
            </button>
            <button
              onClick={cancel}
              className="px-4 py-2 rounded-lg border border-[#EEEEEE] text-sm text-[#6B7280]"
              data-testid="phdir-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={startAdd}
          className="mb-4 px-3 py-1.5 rounded-lg border border-[#0AADA0] text-[#0AADA0] text-[12px] hover:bg-[#F0FDFA]"
          data-testid="phdir-add"
        >
          + Add pharmacy
        </button>
      )}

      <ul className="space-y-1.5 text-[12px]" data-testid="phdir-list">
        {rows.map((row) => (
          <li
            key={row.canonical_key}
            className="flex items-center justify-between gap-3 px-2 py-1.5 rounded bg-[#F9FAFB]"
            data-testid={`phdir-row-${row.canonical_key}`}
          >
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">
                {row.name}
                {row.source === "admin" && (
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#0AADA0]/10 text-[#0AADA0]">
                    admin-edited
                  </span>
                )}
              </div>
              <div className="text-[#6B7280] truncate">
                {row.phone || "— no phone —"} · {row.address || "— no address —"}
                {row.district && (
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#E63946]/10 text-[#E63946]">
                    {row.district}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => startEdit(row)}
                className="text-[11px] text-[#0AADA0] hover:underline"
                data-testid={`phdir-edit-${row.canonical_key}`}
              >
                Edit
              </button>
              <button
                onClick={() => remove(row)}
                className="text-[11px] text-[#E63946] hover:underline"
                data-testid={`phdir-delete-${row.canonical_key}`}
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
};

// -------------- Pharmacy Rotation (2026-08-05) --------------
// Fast weekly-rotation entry — designed to take <60s per Sunday.
// Aruba's on-duty rotation is 2 zones (Pabou di Brug + Pariba di Brug),
// but this form supports 1 or 2 zones so Curaçao/Bonaire/St. Maarten
// can use it too when the admin knows the local rotation source.
//
// Design decisions:
//  - Date fields auto-fill: valid_from defaults to next Sunday, valid_to
//    to next Saturday. Admin can override, but the common Sun-Sat case
//    is one click (or zero — just tab through).
//  - Zone labels pre-fill from the previous week's rotation (if any) so
//    an admin who re-uses the same "Pabou di Brug" / "Pariba di Brug"
//    labels doesn't have to retype them.
//  - "Copy from previous" button on each zone repopulates name/phone/
//    address from the prior week — many rotations cycle through a
//    fixed roster of pharmacies, so this saves a lot of typing.
//  - Single Save button at the bottom; blocks the form while saving.
//  - Recent rotations shown below the form for quick reference /
//    deletion of a mistake.

// Helper: next Sunday's ISO date (local time). If today is Sunday,
// returns TODAY (so an admin filling out "this week's" rotation on
// Sunday morning gets today, not next Sunday).
const _nextSundayIso = () => {
  const now = new Date();
  const dow = now.getDay(); // 0=Sun
  const daysUntilSunday = dow === 0 ? 0 : 7 - dow;
  const target = new Date(now);
  target.setDate(now.getDate() + daysUntilSunday);
  return target.toISOString().slice(0, 10);
};
// Helper: valid_to = valid_from + 6 days (Sun-Sat inclusive)
const _endOfWeekIso = (fromIso) => {
  const d = new Date(fromIso + "T00:00:00");
  d.setDate(d.getDate() + 6);
  return d.toISOString().slice(0, 10);
};

// Default zone labels per island — the labels the admin will almost
// always want. Editable in the form.
const DEFAULT_ZONE_LABELS = {
  aruba: ["Pabou di Brug", "Pariba di Brug"],
  curacao: ["Punda", "Otrobanda"],
  bonaire: ["Kralendijk", ""],
  stmaarten: ["Philipsburg", "French side"],
};

const _blankZone = (label = "") => ({ zone: label, name: "", phone: "", address: "" });

const PharmacyRotationAdmin = ({ island }) => {
  const defaultZones = DEFAULT_ZONE_LABELS[island] || ["", ""];
  const [validFrom, setValidFrom] = useState(_nextSundayIso());
  const [validTo, setValidTo] = useState(_endOfWeekIso(_nextSundayIso()));
  const [zones, setZones] = useState([
    _blankZone(defaultZones[0]),
    _blankZone(defaultZones[1]),
  ]);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);
  const [current, setCurrent] = useState(null);
  const [today, setToday] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/essentials/pharmacy-rotations", {
        params: { island },
      });
      setRecent(r.data.rows || []);
      setCurrent(r.data.current || null);
      setToday(r.data.today || "");
    } catch (_) {
      /* silent */
    }
  };
  useEffect(() => {
    // Reset form to next-week defaults whenever the island changes.
    const from = _nextSundayIso();
    setValidFrom(from);
    setValidTo(_endOfWeekIso(from));
    setZones([_blankZone(defaultZones[0]), _blankZone(defaultZones[1])]);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [island]);

  // Keep valid_to in sync when valid_from changes (Sun-Sat convention),
  // but only if the admin hasn't manually diverged the two dates. If
  // they set a custom range, we don't overwrite their choice.
  const onValidFromChange = (v) => {
    const currentGap = new Date(validTo + "T00:00:00") - new Date(validFrom + "T00:00:00");
    setValidFrom(v);
    // Auto-shift valid_to only for the standard 6-day gap
    if (currentGap === 6 * 86400 * 1000) {
      setValidTo(_endOfWeekIso(v));
    }
  };

  const setZoneField = (i, field, value) => {
    setZones((prev) => prev.map((z, idx) => (idx === i ? { ...z, [field]: value } : z)));
  };

  const copyFromPrevious = (i) => {
    // Prefer the row that's directly before this rotation (index 0 of
    // `recent` is newest; if the newest IS the row we're editing, look
    // at index 1). If nothing there, no-op.
    const source = recent.find((r) => r.valid_from !== validFrom) || null;
    if (!source || !(source.zones || [])[i]) {
      toast.info("No previous rotation to copy from");
      return;
    }
    const src = source.zones[i];
    setZones((prev) => prev.map((z, idx) => (idx === i ? {
      zone: z.zone || src.zone || "",
      name: src.name || "",
      phone: src.phone || "",
      address: src.address || "",
    } : z)));
  };

  const save = async () => {
    // Validate on the client so a fat-finger doesn't need a round-trip
    if (!validFrom || !validTo) {
      toast.error("Both dates are required");
      return;
    }
    if (validFrom > validTo) {
      toast.error("Valid From must be on or before Valid To");
      return;
    }
    const filled = zones.filter((z) => (z.name || "").trim() && (z.phone || "").trim());
    if (!filled.length) {
      toast.error("At least one zone with name + phone is required");
      return;
    }
    setSaving(true);
    try {
      await api.put(
        "/admin/essentials/pharmacy-rotations",
        { valid_from: validFrom, valid_to: validTo, zones: filled },
        { params: { island } },
      );
      toast.success(`Rotation saved for ${validFrom} → ${validTo}`);
      // Reset to a blank NEXT rotation so an admin can chain multiple
      // saves (e.g. entering 3 weeks at once) without page reloads.
      const nextFrom = _endOfWeekIso(validFrom);  // day after end-of-week
      const bump = new Date(nextFrom + "T00:00:00");
      bump.setDate(bump.getDate() + 1);
      const chained = bump.toISOString().slice(0, 10);
      setValidFrom(chained);
      setValidTo(_endOfWeekIso(chained));
      setZones([_blankZone(defaultZones[0]), _blankZone(defaultZones[1])]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (from) => {
    if (!window.confirm(`Delete the rotation starting ${from}?`)) return;
    try {
      await api.delete("/admin/essentials/pharmacy-rotations", {
        params: { island, valid_from: from },
      });
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const isCurrent = (r) => r.valid_from <= today && today <= r.valid_to;

  return (
    <SectionCard
      title="💊 Pharmacy on Duty · Weekly Rotation"
      subtitle="Add each week's on-duty pharmacies. The public card only shows a row when today is inside its Valid From/To window — expired rows never display."
      testid="essentials-admin-pharmacy-rotation"
    >
      {/* Today's status banner — makes it obvious whether the public
          card is currently showing a real rotation, the hospital
          fallback, or the neutral empty state. */}
      <div
        className={`mb-4 rounded-md px-3 py-2 text-[12px] ${
          current
            ? "bg-[#DCFCE7] text-[#166534] border border-[#BBF7D0]"
            : "bg-[#FEE2E2] text-[#7F1D1D] border border-[#FECACA]"
        }`}
        data-testid="pharmacy-rotation-today-status"
      >
        {current ? (
          <>
            ✓ Public card is showing this week's rotation ({current.valid_from} →{" "}
            {current.valid_to}).
          </>
        ) : (
          <>
            ⚠ No valid rotation for today ({today}). Public card is showing the
            hospital-call fallback. Add this week's rotation below.
          </>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <Field
          label="Valid From (Sun)"
          type="date"
          value={validFrom}
          onChange={onValidFromChange}
          testid="phr-valid-from"
        />
        <Field
          label="Valid To (Sat)"
          type="date"
          value={validTo}
          onChange={setValidTo}
          testid="phr-valid-to"
        />
      </div>

      {zones.map((z, i) => (
        <div
          key={i}
          className="mb-4 p-3 rounded-lg border border-[#EEEEEE]"
          data-testid={`phr-zone-block-${i}`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-[#0AADA0] font-semibold">
              Zone {i + 1}
            </div>
            <button
              type="button"
              onClick={() => copyFromPrevious(i)}
              className="text-[11px] text-[#0AADA0] hover:underline"
              data-testid={`phr-copy-prev-${i}`}
            >
              Copy from previous week
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label="Zone label"
              value={z.zone}
              onChange={(v) => setZoneField(i, "zone", v)}
              testid={`phr-zone-${i}-label`}
              placeholder="Pabou di Brug"
            />
            <Field
              label="Pharmacy name *"
              value={z.name}
              onChange={(v) => setZoneField(i, "name", v)}
              testid={`phr-zone-${i}-name`}
            />
            <Field
              label="Phone *"
              value={z.phone}
              onChange={(v) => setZoneField(i, "phone", v)}
              testid={`phr-zone-${i}-phone`}
              placeholder="+297 588 7364"
            />
            <Field
              label="Address"
              value={z.address}
              onChange={(v) => setZoneField(i, "address", v)}
              testid={`phr-zone-${i}-address`}
            />
          </div>
        </div>
      ))}

      <button
        onClick={save}
        disabled={saving}
        className="px-4 py-2 rounded-lg bg-[#0AADA0] text-white text-sm font-medium disabled:opacity-60"
        data-testid="phr-save"
      >
        {saving ? "Saving..." : "Save this week's rotation"}
      </button>

      {recent.length > 0 && (
        <div className="mt-6 pt-4 border-t border-[#EEEEEE]">
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">
            Recent rotations
          </div>
          <ul className="space-y-1.5 text-[12px]" data-testid="phr-recent-list">
            {recent.slice(0, 6).map((r) => (
              <li
                key={r.valid_from}
                className={`flex items-center justify-between gap-3 px-2 py-1.5 rounded ${
                  isCurrent(r) ? "bg-[#DCFCE7]/60" : "bg-[#F9FAFB]"
                }`}
                data-testid={`phr-recent-row-${r.valid_from}`}
              >
                <div className="min-w-0">
                  <span className="font-medium">
                    {r.valid_from} → {r.valid_to}
                  </span>
                  {isCurrent(r) && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#0AADA0] text-white uppercase tracking-wider">
                      Current
                    </span>
                  )}
                  <span className="ml-2 text-[#6B7280] truncate">
                    {(r.zones || []).map((z) => z.name).filter(Boolean).join(" · ")}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => deleteRow(r.valid_from)}
                  className="text-[11px] text-[#E63946] hover:underline shrink-0"
                  data-testid={`phr-delete-${r.valid_from}`}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  );
};

// -------------- Pharmacy --------------
const PharmacyAdmin = ({ island }) => {
  const [f, setF] = useState({
    name: "", address: "", phone: "", open_time: "",
    close_time: "", is_24h: false, valid_date: "", active: true,
  });
  const [saving, setSaving] = useState(false);
  const [updated, setUpdated] = useState("");

  useEffect(() => {
    api.get("/admin/essentials/pharmacy", { params: { island } })
      .then((r) => {
        const d = r.data || {};
        setF({
          name: d.name || "", address: d.address || "", phone: d.phone || "",
          open_time: d.open_time || "", close_time: d.close_time || "",
          is_24h: !!d.is_24h, valid_date: d.valid_date || "",
          active: d.active ?? true,
        });
        setUpdated(d.updated_at || "");
      }).catch(() => {});
  }, [island]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/essentials/pharmacy", f, { params: { island } });
      setUpdated(r.data.updated_at || "");
      toast.success("Pharmacy saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="💊 Pharmacy on Duty"
      subtitle={`Manual entry — expires at midnight of Valid Date.${updated ? ` Last updated: ${new Date(updated).toLocaleString()}` : ""}`}
      testid="essentials-admin-pharmacy"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Pharmacy Name" value={f.name}
               onChange={(v) => setF({ ...f, name: v })}
               testid="ph-name" />
        <Field label="Phone" value={f.phone}
               onChange={(v) => setF({ ...f, phone: v })}
               testid="ph-phone" />
        <div className="sm:col-span-2">
          <Field label="Address" value={f.address}
                 onChange={(v) => setF({ ...f, address: v })}
                 testid="ph-address" />
        </div>
        <Field label="Opening Time" value={f.open_time} placeholder="08:00"
               onChange={(v) => setF({ ...f, open_time: v })}
               testid="ph-open" />
        <Field label="Closing Time" value={f.close_time} placeholder="22:00"
               onChange={(v) => setF({ ...f, close_time: v })}
               testid="ph-close" />
        <Field label="Valid Date" type="date" value={f.valid_date}
               onChange={(v) => setF({ ...f, valid_date: v })}
               testid="ph-date" />
        <div className="flex items-center gap-4 pt-6">
          <Toggle label="Open 24 hours" value={f.is_24h}
                  onChange={(v) => setF({ ...f, is_24h: v })}
                  testid="ph-24h" />
          <Toggle label="Active" value={f.active}
                  onChange={(v) => setF({ ...f, active: v })}
                  testid="ph-active" />
        </div>
      </div>
      <div className="mt-4">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-[#0AADA0] text-white text-sm font-medium disabled:opacity-60"
          data-testid="ph-save"
        >
          {saving ? "Saving..." : "Save Pharmacy"}
        </button>
      </div>
    </SectionCard>
  );
};

// -------------- Sea State (formerly Beach Conditions) --------------
// 2026-07-18: rescoped — flag-color safety-recommendation UI retired.
// Admin can post free-form local advisory text via Manual Override.

const BeachAdmin = ({ island }) => {
  const [f, setF] = useState({
    override: false, flag_color: "green", condition: "",
    wave_height: "", wind: "", water_temp: "", best_beach: "", active: true,
  });
  const [saving, setSaving] = useState(false);
  const [updated, setUpdated] = useState("");
  const [live, setLive] = useState(null);

  useEffect(() => {
    api.get("/admin/essentials/beach", { params: { island } })
      .then((r) => {
        const d = r.data || {};
        setF({
          override: !!d.override,
          flag_color: d.flag_color || "green",
          condition: d.condition || "",
          wave_height: d.wave_height || "",
          wind: d.wind || "",
          water_temp: d.water_temp || "",
          best_beach: d.best_beach || "",
          active: d.active ?? true,
        });
        setUpdated(d.updated_at || "");
      }).catch(() => {});
    api.get("/essentials", { params: { island } })
      .then((r) => setLive(r.data?.beach || null))
      .catch(() => {});
  }, [island]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/essentials/beach", f, { params: { island } });
      setUpdated(r.data.updated_at || "");
      toast.success("Sea state saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const apiStatus = live?.source === "open-meteo" ? "PASSED" : "FAILED";

  return (
    <SectionCard
      title="🌊 Sea State Forecast"
      subtitle="Auto-fetches waves / wind / water temp from Open-Meteo (forecast, not lifeguard data). Enable Manual Override to post a free-form local advisory."
      testid="essentials-admin-beach"
    >
      <div className="mb-4 p-3 bg-[#F5F5F5] rounded-lg text-xs" data-testid="beach-api-status">
        <div className="flex items-center gap-2">
          <span className="uppercase tracking-wider text-[#6B7280]">Auto-fetch:</span>
          <span className={apiStatus === "PASSED" ? "text-[#0AADA0]" : "text-[#E63946]"}>
            {apiStatus}
          </span>
        </div>
        {live && (
          <div className="text-[#6B7280] mt-1">
            Last call: {live.fetched_at || "—"} · Waves {live.wave_height || "—"}
            {live.wind ? ` · Wind ${live.wind}` : ""}
            {live.water_temp ? ` · Water ${live.water_temp}` : ""}
          </div>
        )}
      </div>

      <div className="mb-3">
        <Toggle label="Manual override (post a local advisory / ignore live API)" value={f.override}
                onChange={(v) => setF({ ...f, override: v })}
                testid="beach-override" />
      </div>

      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${f.override ? "" : "opacity-60 pointer-events-none"}`}>
        <Field label="Local advisory text" placeholder="e.g. Rough swell, west side closed by lifeguards" value={f.condition}
               onChange={(v) => setF({ ...f, condition: v })}
               testid="beach-condition" />
        <Field label="Wave height" placeholder="0.8m" value={f.wave_height}
               onChange={(v) => setF({ ...f, wave_height: v })}
               testid="beach-wave" />
        <Field label="Wind" placeholder="20 km/h NE" value={f.wind}
               onChange={(v) => setF({ ...f, wind: v })}
               testid="beach-wind" />
        <Field label="Water temp" placeholder="27°C / 81°F" value={f.water_temp}
               onChange={(v) => setF({ ...f, water_temp: v })}
               testid="beach-water" />
        <Field label="Best beach today" value={f.best_beach}
               onChange={(v) => setF({ ...f, best_beach: v })}
               testid="beach-best" />
      </div>

      <div className="mt-4 flex items-center gap-3">
        <Toggle label="Active" value={f.active}
                onChange={(v) => setF({ ...f, active: v })}
                testid="beach-active" />
        <button
          onClick={save} disabled={saving}
          className="px-4 py-2 rounded-lg bg-[#0AADA0] text-white text-sm font-medium disabled:opacity-60"
          data-testid="beach-save"
        >
          {saving ? "Saving..." : "Save Beach"}
        </button>
      </div>
    </SectionCard>
  );
};

// -------------- Currency --------------
const CurrencyAdmin = ({ island }) => {
  const [sponsorName, setSponsorName] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [live, setLive] = useState(null);

  useEffect(() => {
    // Load existing sponsor for this island via /admin/ads (ad-slot doc)
    api.get("/admin/ads").then((r) => {
      const docs = Array.isArray(r.data) ? r.data : [];
      const match = docs.find(
        (d) => d.slot === "essentials_currency_sponsor" &&
               ((d.island_targeting || "all").toLowerCase() === island)
      );
      if (match) {
        setSponsorName(match.sponsor_name || "");
        setActive(match.active ?? true);
      } else {
        setSponsorName("");
        setActive(true);
      }
    }).catch(() => {});
    // Show current live rate snapshot so operators can sanity-check
    api.get("/essentials/currency", { params: { island } })
      .then((r) => setLive(r.data || null))
      .catch(() => setLive(null));
  }, [island]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/ads/essentials_currency_sponsor", {
        sponsor_name: sponsorName,
        active,
        island_targeting: island,
        image_url: "",
        link_url: "",
        tagline: "",
      });
      toast.success("Currency sponsor saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const rateSummary = live && !live.unavailable
    ? `Base ${live.base_currency} · ${Object.entries(live.rates || {})
        .map(([k, v]) => `${k}:${v}`).join(" · ")}${live.is_cached ? " · CACHED" : ""}`
    : "Rates unavailable";

  return (
    <SectionCard
      title="💱 Currency Exchange"
      subtitle="Live rates auto-fetched from open.er-api.com (6h cache). AWG/USD is legally pegged to 1.79. Only sponsor is editable here."
      testid="essentials-admin-currency"
    >
      <div className="mb-4 p-3 bg-[#F5F5F5] rounded-lg text-xs" data-testid="currency-api-status">
        <div className="flex items-center gap-2">
          <span className="uppercase tracking-wider text-[#6B7280]">Auto-fetch:</span>
          <span className={live && !live.unavailable ? "text-[#0AADA0]" : "text-[#E63946]"}>
            {live && !live.unavailable ? "PASSED" : "FAILED"}
          </span>
        </div>
        <div className="text-[#6B7280] mt-1 break-all">{rateSummary}</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Sponsor Name" value={sponsorName}
               onChange={setSponsorName} testid="cur-sponsor-name" />
        <div className="flex items-center pt-6">
          <Toggle label="Active" value={active}
                  onChange={setActive} testid="cur-active" />
        </div>
      </div>

      <div className="mt-4">
        <button
          onClick={save} disabled={saving}
          className="px-4 py-2 rounded-lg bg-[#0AADA0] text-white text-sm font-medium disabled:opacity-60"
          data-testid="cur-save"
        >
          {saving ? "Saving..." : "Save Currency Sponsor"}
        </button>
      </div>
    </SectionCard>
  );
};

// -------------- Cruise --------------
// Shows THREE things:
// 1. Live scraper health (replaces old hardcoded "AUTO-SCRAPE: FAILED" banner)
// 2. Scraped entries from db.cruise_scraped (what the public card actually renders)
// 3. Manual entries from db.essentials_cruise (admin-owned overrides)
const CruiseAdmin = ({ island }) => {
  const [entries, setEntries] = useState([]);
  const [scraped, setScraped] = useState([]);
  const [health, setHealth] = useState(null);
  const [draft, setDraft] = useState({
    date: "", ship_name: "", cruise_line: "",
    arrives: "", departs: "", passengers: 0,
  });
  const [saving, setSaving] = useState(false);

  const reload = () => {
    api.get("/admin/essentials/cruise", { params: { island } })
      .then((r) => {
        setEntries(r.data?.entries || []);
        setScraped(r.data?.scraped || []);
        setHealth(r.data?.scraper_health || null);
      })
      .catch(() => { setEntries([]); setScraped([]); setHealth(null); });
  };

  useEffect(() => { reload(); }, [island]);

  const add = async () => {
    if (!draft.date || !draft.ship_name) {
      toast.error("Date and ship name required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/essentials/cruise",
        { ...draft, passengers: parseInt(draft.passengers || 0, 10) },
        { params: { island } });
      setDraft({ date: "", ship_name: "", cruise_line: "",
                 arrives: "", departs: "", passengers: 0 });
      reload();
      toast.success("Ship added");
    } catch (e) { toast.error("Add failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    await api.delete(`/admin/essentials/cruise/${id}`);
    reload();
  };

  // Derive banner state from live scraper health.
  const scraperIslandOk = (health?.islands_ok || []).includes(island);
  const scraperIslandFailed = (health?.islands_failed || []).includes(island);
  const overallStatus = health?.last_status || "unknown";
  let bannerLabel, bannerColor;
  if (overallStatus === "ok" && scraperIslandOk) {
    bannerLabel = "OK"; bannerColor = "#0AADA0";
  } else if (overallStatus === "partial" && scraperIslandOk) {
    bannerLabel = "OK (other islands partial)"; bannerColor = "#0AADA0";
  } else if (scraperIslandFailed) {
    bannerLabel = "FAILED for this island"; bannerColor = "#E63946";
  } else if (overallStatus === "failed") {
    bannerLabel = "FAILED"; bannerColor = "#E63946";
  } else if (overallStatus === "unknown") {
    bannerLabel = "Never run"; bannerColor = "#6B7280";
  } else {
    bannerLabel = overallStatus.toUpperCase(); bannerColor = "#6B7280";
  }
  const lastSuccessLabel = health?.last_success_at
    ? new Date(health.last_success_at).toLocaleString()
    : "never";
  const consecutiveFails = health?.consecutive_failures || 0;

  return (
    <SectionCard
      title="🚢 Cruise Ships in Port"
      subtitle="The public card is powered by the CruiseMapper scraper. Manual entries below override the scraper by (date, ship name)."
      testid="essentials-admin-cruise"
    >
      <div className="mb-4 p-3 bg-[#F5F5F5] rounded-lg text-xs" data-testid="cruise-scrape-status">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="uppercase tracking-wider text-[#6B7280]">Auto-scrape:</span>
          <span style={{ color: bannerColor, fontWeight: 600 }}
                data-testid="cruise-scrape-status-label">
            {bannerLabel}
          </span>
          <span className="text-[#6B7280]">·</span>
          <span className="text-[#6B7280]">Last success: {lastSuccessLabel}</span>
          {consecutiveFails > 0 && (
            <>
              <span className="text-[#6B7280]">·</span>
              <span className="text-[#E63946]">{consecutiveFails} consecutive failures</span>
            </>
          )}
        </div>
        <div className="text-[#6B7280] mt-1">
          Source: CruiseMapper. Nightly cron. Manual entries below override matched rows.
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 items-end mb-3">
        <Field label="Date" type="date" value={draft.date}
               onChange={(v) => setDraft({ ...draft, date: v })}
               testid="cruise-date" />
        <Field label="Ship Name" value={draft.ship_name}
               onChange={(v) => setDraft({ ...draft, ship_name: v })}
               testid="cruise-ship" />
        <Field label="Cruise Line" value={draft.cruise_line}
               onChange={(v) => setDraft({ ...draft, cruise_line: v })}
               testid="cruise-line" />
        <Field label="Arrives" placeholder="08:00" value={draft.arrives}
               onChange={(v) => setDraft({ ...draft, arrives: v })}
               testid="cruise-arrives" />
        <Field label="Departs" placeholder="18:00" value={draft.departs}
               onChange={(v) => setDraft({ ...draft, departs: v })}
               testid="cruise-departs" />
        <Field label="Passengers" type="number" value={draft.passengers}
               onChange={(v) => setDraft({ ...draft, passengers: v })}
               testid="cruise-pax" />
      </div>
      <button
        onClick={add} disabled={saving}
        className="px-4 py-2 rounded-lg bg-[#0AADA0] text-white text-sm font-medium disabled:opacity-60"
        data-testid="cruise-add"
      >
        {saving ? "Adding..." : "+ Add Ship"}
      </button>

      <div className="mt-6" data-testid="cruise-manual-section">
        <h4 className="text-[12px] uppercase tracking-wider text-[#6B7280] mb-2">
          Manual entries ({entries.length})
        </h4>
        <div className="overflow-x-auto" data-testid="cruise-entries-table">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="text-[11px] uppercase text-[#6B7280] tracking-wider border-b border-[#EEEEEE]">
                <th className="py-2 pr-2">Date</th>
                <th className="py-2 pr-2">Ship</th>
                <th className="py-2 pr-2">Line</th>
                <th className="py-2 pr-2">Arr</th>
                <th className="py-2 pr-2">Dep</th>
                <th className="py-2 pr-2">Pax</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-3 text-xs text-[#6B7280]">
                    No manual overrides for {island}. The public card uses the scraped rows below.
                  </td>
                </tr>
              )}
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-[#F5F5F5] text-[13px]">
                  <td className="py-1.5 pr-2 whitespace-nowrap">{e.date}</td>
                  <td className="py-1.5 pr-2 font-medium">{e.ship_name}</td>
                  <td className="py-1.5 pr-2 text-[#6B7280]">{e.cruise_line}</td>
                  <td className="py-1.5 pr-2">{e.arrives}</td>
                  <td className="py-1.5 pr-2">{e.departs}</td>
                  <td className="py-1.5 pr-2 text-[#0AADA0]">{e.passengers}</td>
                  <td className="py-1.5 pr-2 text-right">
                    <button
                      onClick={() => del(e.id)}
                      className="text-xs text-[#E63946]"
                      data-testid={`cruise-del-${e.id}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6" data-testid="cruise-scraped-section">
        <h4 className="text-[12px] uppercase tracking-wider text-[#6B7280] mb-2">
          Scraped entries — next 14 days ({scraped.length})
          <span className="ml-2 normal-case text-[10px] text-[#9CA3AF] font-normal">
            (read-only · from CruiseMapper · this is what the public card shows)
          </span>
        </h4>
        <div className="overflow-x-auto" data-testid="cruise-scraped-table">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="text-[11px] uppercase text-[#6B7280] tracking-wider border-b border-[#EEEEEE]">
                <th className="py-2 pr-2">Date</th>
                <th className="py-2 pr-2">Ship</th>
                <th className="py-2 pr-2">Line</th>
                <th className="py-2 pr-2">Arr</th>
                <th className="py-2 pr-2">Dep</th>
                <th className="py-2 pr-2">Pax</th>
              </tr>
            </thead>
            <tbody>
              {scraped.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-3 text-xs text-[#6B7280]">
                    No scraped ships for {island} in the next 14 days.
                  </td>
                </tr>
              )}
              {scraped.map((e, i) => (
                <tr key={`${e.date}-${e.ship_name}-${i}`}
                    className="border-b border-[#F5F5F5] text-[13px]">
                  <td className="py-1.5 pr-2 whitespace-nowrap">{e.date}</td>
                  <td className="py-1.5 pr-2 font-medium">{e.ship_name}</td>
                  <td className="py-1.5 pr-2 text-[#6B7280]">{e.cruise_line}</td>
                  <td className="py-1.5 pr-2">{e.arrives || "—"}</td>
                  <td className="py-1.5 pr-2">{e.departs || "—"}</td>
                  <td className="py-1.5 pr-2 text-[#6B7280]"
                      title={e.passengers === 0 ? "Not published by CruiseMapper" : ""}>
                    {e.passengers === 0 ? "—" : e.passengers}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SectionCard>
  );
};
